import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Flex, Text, TooltipTrigger, Tooltip, failHatchStyle } from '@chatsdet/ui';
import { Check, X, CircleNotch, Clock, Warning } from '@phosphor-icons/react';
import type { WorkflowTestRunStatus } from '../../features/workflows/useAgenticRunDetail';
import { formatDuration, formatRelativeTimeAgo } from '../../features/workflows/useTestRunDetail';

type RunSummary = {
  id: string;
  status: WorkflowTestRunStatus;
  startedAt: string | null;
  finishedAt: string | null;
};

type RunHistoryBarProps = {
  runs: RunSummary[];
  currentRunId: string;
};

const statusConfig: Record<
  WorkflowTestRunStatus,
  {
    color: string;
    activeColor: string;
    label: string;
    icon: React.ReactNode;
  }
> = {
  success: {
    color: 'bg-status-success',
    activeColor: 'bg-status-success ring-2 ring-status-success/30',
    label: 'Passed',
    icon: <Check size={12} weight="bold" className="text-status-success" />,
  },
  failed: {
    color: 'bg-status-failed',
    activeColor: 'bg-status-failed ring-2 ring-status-failed/30',
    label: 'Failed',
    icon: <X size={12} weight="bold" className="text-status-failed" />,
  },
  inconclusive: {
    color: 'bg-muted-foreground/60',
    activeColor: 'bg-muted-foreground/60 ring-2 ring-muted-foreground/20',
    label: 'Platform error',
    icon: <Warning size={12} weight="bold" className="text-muted-foreground" />,
  },
  skipped: {
    color: 'bg-status-skipped',
    activeColor: 'bg-status-skipped ring-2 ring-status-skipped/30',
    label: 'Skipped',
    icon: <Warning size={12} weight="bold" className="text-status-skipped" />,
  },
  flaky_success: {
    color: 'bg-status-flaky',
    activeColor: 'bg-status-flaky ring-2 ring-status-flaky/30',
    label: 'Passed (retry)',
    icon: <Warning size={12} weight="bold" className="text-status-flaky" />,
  },
  running: {
    color: 'bg-status-running animate-pulse',
    activeColor: 'bg-status-running animate-pulse ring-2 ring-status-running/30',
    label: 'Running',
    icon: <CircleNotch size={12} weight="bold" className="text-status-running animate-spin" />,
  },
  queued: {
    color: 'bg-status-queued/40',
    activeColor: 'bg-status-queued/40 ring-2 ring-status-queued/20',
    label: 'Queued',
    icon: <Clock size={12} className="text-status-queued" />,
  },
  waiting: {
    color: 'bg-status-waiting/40',
    activeColor: 'bg-status-waiting/40 ring-2 ring-status-waiting/20',
    label: 'Waiting',
    icon: <Clock size={12} className="text-status-waiting" />,
  },
  canceled: {
    color: 'bg-status-canceled/40',
    activeColor: 'bg-status-canceled/40 ring-2 ring-status-canceled/20',
    label: 'Canceled',
    icon: <X size={12} className="text-status-canceled" />,
  },
  timeout: {
    color: 'bg-status-timeout',
    activeColor: 'bg-status-timeout ring-2 ring-status-timeout/30',
    label: 'Timed Out',
    icon: <Warning size={12} weight="bold" className="text-status-timeout" />,
  },
  degraded: {
    color: 'bg-status-degraded',
    activeColor: 'bg-status-degraded ring-2 ring-status-degraded/30',
    label: 'Degraded',
    icon: <Warning size={12} weight="bold" className="text-status-degraded" />,
  },
};

// Fallback for any status not present in the map (e.g. a newly added DB enum
// value the frontend hasn't been taught about yet) so rendering never crashes.
const fallbackConfig: (typeof statusConfig)[WorkflowTestRunStatus] = {
  color: 'bg-status-skipped',
  activeColor: 'bg-status-skipped ring-2 ring-status-skipped/30',
  label: 'Unknown',
  icon: <Warning size={12} weight="bold" className="text-status-skipped" />,
};

const getStatusConfig = (status: WorkflowTestRunStatus) => statusConfig[status] ?? fallbackConfig;

const RunTooltipContent: React.FC<{
  run: RunSummary;
  runNumber: number;
}> = ({ run, runNumber }) => {
  const config = getStatusConfig(run.status);
  const duration = formatDuration(run.startedAt, run.finishedAt);
  const startedAgo = formatRelativeTimeAgo(run.startedAt);

  return (
    <div className="space-y-1.5 min-w-[140px]">
      <div className="flex items-center justify-between gap-3">
        <Text size="sm" weight="medium">
          Run {runNumber}
        </Text>
        <div className="flex items-center gap-1">
          {config.icon}
          <Text size="xs" className="text-muted-foreground">
            {config.label}
          </Text>
        </div>
      </div>
      <div className="flex items-center justify-between text-muted-foreground">
        <Text size="xs">Duration</Text>
        <Text size="xs">{duration}</Text>
      </div>
      <div className="flex items-center justify-between text-muted-foreground">
        <Text size="xs">Started</Text>
        <Text size="xs">{startedAgo}</Text>
      </div>
    </div>
  );
};

export const RunHistoryBar: React.FC<RunHistoryBarProps> = ({ runs, currentRunId }) => {
  const navigate = useNavigate();

  // Sort by startedAt ascending (oldest first)
  const sortedRuns = React.useMemo(() => {
    return [...runs].sort((a, b) => {
      const aTime = a.startedAt ? new Date(a.startedAt).getTime() : 0;
      const bTime = b.startedAt ? new Date(b.startedAt).getTime() : 0;
      return aTime - bTime;
    });
  }, [runs]);

  // Find current run info
  const currentRunIndex = sortedRuns.findIndex((r) => r.id === currentRunId);
  const currentRun = sortedRuns[currentRunIndex];
  const currentConfig = currentRun ? getStatusConfig(currentRun.status) : null;

  // Build summary text
  const summaryParts: string[] = [];
  if (sortedRuns.length > 1) {
    summaryParts.push(`Run ${currentRunIndex + 1} of ${sortedRuns.length}`);
  }
  if (currentConfig) {
    summaryParts.push(currentConfig.label);
  }
  if (currentRun) {
    summaryParts.push(formatDuration(currentRun.startedAt, currentRun.finishedAt));
  }

  const handleRunClick = (runId: string) => {
    if (runId !== currentRunId) {
      navigate({
        to: '/runs/tests/$testRunId',
        params: { testRunId: runId },
      });
    }
  };

  // Don't render if only one run with no meaningful status
  if (sortedRuns.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-muted/30 border border-border rounded-none">
      {/* Status indicators */}
      {sortedRuns.length > 1 && (
        <Flex align="center" gap="xs">
          {sortedRuns.map((run, index) => {
            const config = getStatusConfig(run.status);
            const isCurrent = run.id === currentRunId;

            return (
              <TooltipTrigger key={run.id} delay={200}>
                <button
                  type="button"
                  onClick={() => handleRunClick(run.id)}
                  className={`
                    w-2.5 h-2.5 rounded-none transition-all
                    ${isCurrent ? config.activeColor : config.color}
                    ${isCurrent ? 'scale-125' : 'hover:scale-110 opacity-70 hover:opacity-100'}
                    ${run.id !== currentRunId ? 'cursor-pointer' : 'cursor-default'}
                  `}
                  // Failed runs carry a diagonal hatch so they differ from pass without color.
                  style={failHatchStyle(run.status === 'failed')}
                  aria-label={`Run ${index + 1}: ${config.label}`}
                />
                <Tooltip variant="light">
                  <RunTooltipContent run={run} runNumber={index + 1} />
                </Tooltip>
              </TooltipTrigger>
            );
          })}
        </Flex>
      )}

      {/* Summary text */}
      <Flex align="center" gap="xs" className="text-muted-foreground">
        {currentConfig && <span className="flex items-center gap-1">{currentConfig.icon}</span>}
        <Text size="sm">{summaryParts.join(' · ')}</Text>
      </Flex>
    </div>
  );
};
